import { PureComponent, type ReactNode } from 'react';

import classNames from 'classnames';
import _isEqual from 'lodash-es/isEqual';

import type { FilesKeyframeSchemaWithIdNullable } from '@iconik-io/iconik-api/src/services/files';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import Icon, { ICONS } from '../../icons/icon';
import { ui } from '../../utils/helpers';
import CollectionKeyframe from './collection_keyframe';
import {
  findCollectionKeyframeUrl,
  findKeyframeUrl,
  getCustomKeyframe,
  getKeyframeType,
} from './utils';

import styles from './collection_keyframes.module.css';

const positionsDefault = [styles.position1, styles.position2, styles.position3];

type Props = {
  childrenUpdate: SearchItemEntity[];
  keyframes: FilesKeyframeSchemaWithIdNullable[];
  className?: string;
  customKeyframeClassName?: string;
  waiting?: boolean;
};

type State = {
  sources: (FilesKeyframeSchemaWithIdNullable[] | undefined)[];
  updated?: boolean;
  positions: string[];
};

class CollectionKeyframes extends PureComponent<Props, State> {
  static defaultProps = {
    childrenUpdate: [],
    keyframes: [],
  };

  constructor(props: Props) {
    super(props);
    this.state = this.getKeyframesUpdate(
      this.props.childrenUpdate
        .filter((object) => object && object.object_type === 'assets')
        .map((update) => update.keyframes ?? undefined),
    );
  }

  componentDidUpdate(prevProps: Readonly<Props>): void {
    if (
      this.props.childrenUpdate &&
      this.props.childrenUpdate !== prevProps.childrenUpdate
    ) {
      if (!_isEqual(this.props.childrenUpdate, prevProps.childrenUpdate)) {
        this.updateKeyframes();
      }
    }

    if (prevProps.waiting !== this.props.waiting) {
      this.handleUpdatedCheckMark();
    }
  }

  setUpdated(updated: boolean): void {
    this.setState({ updated });
  }

  handleUpdatedCheckMark(): void {
    if (this.props.childrenUpdate.length > 0) {
      if (this.props.waiting) {
        this.setUpdated(false);
      }

      if (!this.props.waiting) {
        this.setUpdated(true);
      }
    }
  }

  updateKeyframes(): void {
    this.setState((_state, props) => {
      const updates = props.childrenUpdate.filter(
        (object) => object && object.object_type === 'assets',
      );
      const keyframesData = this.getKeyframesUpdate(
        updates.map((update) => update.keyframes ?? undefined),
      );
      return { ...keyframesData };
    });
  }

  getKeyframesUpdate(
    src: (FilesKeyframeSchemaWithIdNullable[] | undefined)[],
  ): State {
    // sources will use sources from props and continue with the original source
    const sources = src.filter(
      (keyframes) => findKeyframeUrl(keyframes) !== undefined,
    );
    // positions will use contain position for sources and continue with default positions
    const positions = [
      ...sources.map(() => styles.contain),
      ...positionsDefault,
    ];
    positions.splice(3, positions.length - 1);
    return {
      sources,
      positions,
    };
  }

  getSource(index: number): string | undefined {
    if (this.state.sources[index]) {
      return findKeyframeUrl(this.state.sources[index]);
    }
    return findCollectionKeyframeUrl(this.props.keyframes);
  }

  render(): ReactNode {
    const { className, customKeyframeClassName } = this.props;
    const { positions, updated } = this.state;
    const customKeyframe = getCustomKeyframe(this.props.keyframes);
    const width = customKeyframe?.resolution?.width ?? 0;
    const height = customKeyframe?.resolution?.height ?? 0;
    const keyframeType = getKeyframeType(this.props.keyframes);
    return (
      <div
        className={classNames(
          styles.root,
          customKeyframe ? customKeyframeClassName : className,
          {
            [styles.keyframe_map]: keyframeType === 'KEYFRAME_MAP',
          },
        )}
      >
        {customKeyframe ? (
          <div
            {...ui('customkeyframe')}
            className={classNames({
              [styles.custom_keyframe]: true,
              [styles.portrait]: height >= width,
              [styles.landscape]: height < width,
              [styles.new_custom_keyframe]: updated,
            })}
            style={{ backgroundImage: `url(${customKeyframe.url})` }}
          />
        ) : (
          <>
            <CollectionKeyframe
              className={classNames(styles.main, positions[0])}
              source={this.getSource(0)}
            />
            <div className={styles.secondary}>
              <CollectionKeyframe
                className={classNames(styles.secondary_item, positions[1])}
                source={this.getSource(1)}
              />
              <CollectionKeyframe
                className={classNames(styles.secondary_item, positions[2])}
                source={this.getSource(2)}
              />
            </div>
          </>
        )}
        {updated && (
          <div
            className={styles.update}
            {...ui('tick')}
            role="status"
            aria-label="Updated"
          >
            <Icon icon={ICONS.TickCheckedFilled} size={30} />
          </div>
        )}
      </div>
    );
  }
}

export default CollectionKeyframes;
